// The run's trading-channel dimension, as MM Web needs to read it
// (SM-470, consuming SM-450's RunConfig fields).
//
// A run is single-mode by construction, and its currencies are DERIVED
// from the fixed channel policy — an operator never picks a currency
// pair. That is the whole reason this file is four small functions
// rather than a form: CASH debits and pays GOLD; GAME debits RED and
// pays GREEN; there is no third combination and no way to author one.

// Relative, extensioned imports: this console's test runner is
// `node --experimental-strip-types`, which has no bundler alias
// table, and a rule that cannot be executed by a test is a rule
// nobody is protecting.
import type { CurrencyCode } from "./currency.ts";
import type {
  MMMakerAccount,
  MMMarketSummary,
  MMModeReadiness,
  MMRunConfig,
  TradingMode,
} from "./types.ts";

// The enum itself lives with the wire types (SM-525) so types.ts can
// use it without importing this module back. Re-exported here because
// this is where callers look for anything mode-shaped.
export { TRADING_MODES, type TradingMode } from "./types.ts";

/**
 * G3 at the presentation boundary: an absent trading_mode is CASH.
 *
 * Every preset and run snapshot written before 05b has no mode field,
 * and every one of them was a CASH run. Reading absent as GAME would
 * retroactively relabel the entire library.
 */
export function effectiveTradingMode(config: Partial<MMRunConfig> | undefined): TradingMode {
  return config?.trading_mode === "GAME" ? "GAME" : "CASH";
}

/** What this run's BUY orders reserve. */
export function debitCurrency(mode: TradingMode): CurrencyCode {
  return mode === "GAME" ? "RED" : "GOLD";
}

/** What this run's sell / redeem proceeds are credited in. */
export function payoutCurrency(mode: TradingMode): CurrencyCode {
  return mode === "GAME" ? "GREEN" : "GOLD";
}

/**
 * Why a GAME run cannot start yet, or null when it can.
 *
 * GAME participation is never implied, defaulted or inherited (SM-450):
 * the config must name the mode AND carry the explicit opt-in, and this
 * console must not offer the mode at all until its surface flag is on.
 * The server refuses either way — this is so the operator finds out
 * before the run, not from a rejected start.
 */
export function gameBlockReason(
  config: Partial<MMRunConfig> | undefined,
  surfaceEnabled: boolean,
): "surface-disabled" | "no-opt-in" | null {
  if (effectiveTradingMode(config) !== "GAME") return null;
  if (!surfaceEnabled) return "surface-disabled";
  if (!config?.game_opt_in) return "no-opt-in";
  return null;
}

/**
 * The execution modes a market may be quoted in.
 *
 * G3 at the presentation boundary, exactly as `effectiveTradingMode`
 * is for configs: a row with no `trading_modes` came from a server that
 * predates mode tagging, and every market such a server listed was a
 * CASH market. Defaulting to CASH keeps those rows selectable in a
 * CASH preset; defaulting to "both" would offer GAME execution on
 * markets that have no GAME book at all.
 */
export function marketModes(market: Pick<MMMarketSummary, "trading_modes">): TradingMode[] {
  const modes = market.trading_modes;
  return modes && modes.length > 0 ? modes : ["CASH"];
}

/** Whether a market may be selected into a preset running `mode`. */
export function marketAllowsMode(
  market: Pick<MMMarketSummary, "trading_modes">,
  mode: TradingMode,
): boolean {
  return marketModes(market).includes(mode);
}

/**
 * Whether an account's funding reserve holds the wallets `mode` needs,
 * and what is missing when it does not (SM-526).
 *
 * ABSENT READINESS MEANS SELECTABLE. The server omits readiness for an
 * unlinked account and whenever the ledger could not be read, so
 * `undefined` is "not known" — and treating not-known as unready would
 * empty the operator's picker on a ledger blip. The start still
 * refuses, naming the account and the currency, which is the right
 * place for an answer nobody can compute here.
 *
 * CASH readiness is never evidence of GAME readiness: GAME debits RED
 * and pays out GREEN, two wallets a CASH-only reserve has never
 * needed. That is why this takes the mode rather than reading a single
 * "ready" field — the account QA selected was perfectly CASH-ready.
 */
export function reserveReadinessFor(
  account: Pick<MMMakerAccount, "mode_readiness">,
  mode: TradingMode,
): MMModeReadiness | null {
  return account.mode_readiness?.find((r) => r.trading_mode === mode) ?? null;
}

/**
 * Why this account cannot be selected for `mode`, or null when it can.
 *
 * Returns the missing currencies rather than the server's sentence so
 * the caller can render it in the operator's language; the server's
 * `reason` is the fallback when it named something we cannot rebuild.
 */
export function reserveBlocksMode(
  account: Pick<MMMakerAccount, "mode_readiness">,
  mode: TradingMode,
): { missing: string[]; reason: string } | null {
  const readiness = reserveReadinessFor(account, mode);
  if (!readiness || readiness.ready) return null;
  return {
    missing: readiness.missing_currencies ?? [],
    reason: readiness.reason ?? "",
  };
}
